import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Agent } from "@/types/aiToolkit";
import { userAgentSkillsService } from "@/services/userAgentSkillsService";
import {
  SkillBuildForm,
  defaultSkillFormValues,
  validateSkillForm,
  type SkillFormValues,
} from "@/components/agents/SkillBuildForm";

function buildInstructionsFromAgent(agent: Partial<Agent>): string {
  const parts = [agent.customInstructions?.trim(), agent.persona?.systemPrompt?.trim()].filter(
    Boolean
  ) as string[];
  const seen = new Set<string>();
  return parts
    .filter((p) => {
      if (seen.has(p)) return false;
      seen.add(p);
      return true;
    })
    .join("\n\n");
}

export interface SaveAsSkillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent: Partial<Agent>;
  onSkillCreated?: (skillId: string) => void;
}

export function SaveAsSkillDialog({
  open,
  onOpenChange,
  agent,
  onSkillCreated,
}: SaveAsSkillDialogProps) {
  const { toast } = useToast();
  const [form, setForm] = useState<SkillFormValues & { outputSchemaDraftInvalid?: boolean }>(
    defaultSkillFormValues
  );

  useEffect(() => {
    if (!open) return;
    setForm({
      name: agent.name ? `${agent.name} Skill` : "",
      description: agent.description ?? "",
      instructions: buildInstructionsFromAgent(agent),
      integrationScopes: agent.integrationScopes ?? [],
      outputSchema: agent.outputSchema,
    });
  }, [open, agent]);

  const error = validateSkillForm(form);

  const saveMutation = useMutation({
    mutationFn: async () => {
      return await userAgentSkillsService.create({
        name: form.name.trim(),
        description: form.description.trim() || null,
        instructions: form.instructions.trim(),
        integrationScopes: form.integrationScopes,
        outputSchema: form.outputSchema ?? null,
        isTemplate: false,
      });
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["user-agent-skills"] });
      toast({ title: "Skill saved", description: `"${created.name}" is ready to attach to agents.` });
      onSkillCreated?.(created.id);
      onOpenChange(false);
    },
    onError: (e: Error) => {
      toast({ title: "Failed to save skill", description: e.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Save instructions as skill</DialogTitle>
        </DialogHeader>
        <SkillBuildForm value={form} onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))} />
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!!error || saveMutation.isPending}
          >
            {saveMutation.isPending ? "Saving..." : "Save Skill"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
